import { useEffect, useState } from "react";
import { Outlet, Link, useParams } from "react-router-dom";
import { apiClient } from "../../services/apiClientService";
import { useVendorStore } from "../../context/VendorStoreContext";

/**
 * Layout wrapper for all /:storeSlug/* routes.
 *
 * - Fetches the vendor by slug once and pushes it into VendorStoreContext so
 *   Header, About, Contact, StorefrontDocumentTitle all react automatically.
 * - Clears the context on unmount so navigating back to the main site resets branding.
 * - Child routes receive the vendor via <Outlet /> + context; they never need to
 *   re-fetch the vendor themselves.
 */
export default function VendorStoreLayout() {
  const { storeSlug } = useParams();
  const { _setVendorStore } = useVendorStore();

  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    if (!storeSlug) {
      setNotFound(true);
      setLoading(false);
      return;
    }

    let cancelled = false;
    setLoading(true);
    setNotFound(false);
    _setVendorStore({ vendorStore: null, storeSlug });

    apiClient("GET", `/api/vendors/store/${encodeURIComponent(storeSlug)}`, null, {
      skipLoading: true,
    })
      .then((data) => {
        if (cancelled) return;
        if (data?.vendor) {
          _setVendorStore({ vendorStore: data.vendor, storeSlug });
          // Persist so transitional pages (login, wishlist…) keep the right slug
          sessionStorage.setItem("mmd_storeSlug", storeSlug);
          setLoading(false);
        } else {
          _setVendorStore({ vendorStore: null, storeSlug: null });
          sessionStorage.removeItem("mmd_storeSlug");
          setNotFound(true);
          setLoading(false);
        }
      })
      .catch(() => {
        if (cancelled) return;
        _setVendorStore({ vendorStore: null, storeSlug: null });
        sessionStorage.removeItem("mmd_storeSlug");
        setNotFound(true);
        setLoading(false);
      });

    return () => {
      cancelled = true;
      // Clear context but keep sessionStorage so Header keeps correct links
      // on transitional pages (login, wishlist, etc.)
      _setVendorStore({ vendorStore: null, storeSlug: null });
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [storeSlug]);

  if (loading) {
    return (
      <div className="flex min-h-[60vh] items-center justify-center">
        <div className="h-8 w-8 animate-spin rounded-full border-2 border-rose-400 border-t-transparent" />
      </div>
    );
  }

  if (notFound) {
    return (
      <div className="flex min-h-[60vh] flex-col items-center justify-center gap-4 text-center px-4">
        <p className="text-2xl font-bold text-gray-800">Store not found</p>
        <p className="text-gray-500">
          The store{" "}
          <span className="font-mono text-rose-600">/{storeSlug}</span> doesn&apos;t
          exist or is currently unavailable.
        </p>
        <Link
          to="/"
          className="mt-2 rounded-lg bg-rose-600 px-5 py-2 text-sm font-semibold text-white hover:bg-rose-700"
        >
          Go to homepage
        </Link>
      </div>
    );
  }

  return <Outlet />;
}
